// Which vehicle types a vehicle-check checklist item applies to. An item
// with no rows here applies to every vehicle type (unchanged default
// behaviour); an item with one or more rows applies ONLY to those types --
// e.g. a "Wheelchair hoist photo" item scoped to just the Wheelchair Van
// type. Same junction-table shape as vehicle_type_compatibility.
import { supabase } from "@/lib/supabase";

/** item_key -> set of vehicle_type it's restricted to. Absent key = unrestricted (all types). */
export type CheckItemTypeRestrictions = Map<string, Set<string>>;

export async function loadCheckItemTypeRestrictions(): Promise<CheckItemTypeRestrictions> {
  const { data, error } = await supabase.from("vehicle_check_item_types").select("item_key, vehicle_type");
  if (error) {
    console.error("Error loading check item type restrictions:", error);
    return new Map();
  }
  const map: CheckItemTypeRestrictions = new Map();
  for (const row of data || []) {
    if (!map.has(row.item_key)) map.set(row.item_key, new Set());
    map.get(row.item_key)!.add(row.vehicle_type);
  }
  return map;
}

/** Whether a check item applies to the given vehicle type (unrestricted items apply to everything). */
export function checkItemAppliesToType(
  itemKey: string,
  vehicleType: string | null | undefined,
  restrictions: CheckItemTypeRestrictions
): boolean {
  const restricted = restrictions.get(itemKey);
  if (!restricted || restricted.size === 0) return true;
  return !!vehicleType && restricted.has(vehicleType);
}

/** Replace-all: sets an item's full restriction set to exactly the given vehicle types (empty array = unrestricted). */
export async function setCheckItemTypeRestrictions(itemKey: string, vehicleTypes: string[]): Promise<void> {
  const { error: deleteError } = await supabase.from("vehicle_check_item_types").delete().eq("item_key", itemKey);
  if (deleteError) throw deleteError;

  if (vehicleTypes.length === 0) return;

  const { error: insertError } = await supabase
    .from("vehicle_check_item_types")
    .insert(vehicleTypes.map((vehicle_type) => ({ item_key: itemKey, vehicle_type })));
  if (insertError) throw insertError;
}
